// SA-3
import type { ProductionWorkerRecord, ShiftRecord } from '../types/records'
import { isPositionShift5_2 } from './productionWorkers'
import { isRotationShift, isShift5_2 } from './shifts'
import {
  type CalendarBrigadeMap,
  isWeekdayMonFri,
  normalizeRelationId,
  parseDateKey,
  resolvePlantIsWorking,
} from './productionCalendar'

export type { CalendarBrigadeMap }

/** ID смены 1/2, работающей в этот день, или null (простой / бригада не задана). */
export function resolveWorkingBrigade(
  dateKey: string,
  calendarMap: CalendarBrigadeMap,
): string | null {
  if (!resolvePlantIsWorking(dateKey, calendarMap)) return null
  const rec = calendarMap.get(dateKey)
  const brigade = normalizeRelationId(rec?.workingBrigade)
  return brigade || null
}

export function brigadeNotSetHint(dateKey: string, calendarMap: CalendarBrigadeMap): string | null {
  if (!resolvePlantIsWorking(dateKey, calendarMap)) return null
  const rec = calendarMap.get(dateKey)
  if (rec && !normalizeRelationId(rec.workingBrigade)) {
    return 'В календаре не указана рабочая бригада 2/2 на этот день — откройте производственный календарь и выберите Смену 1 или 2.'
  }
  if (!rec) {
    return 'В календаре не указана рабочая бригада 2/2 на этот день — откройте производственный календарь и выберите Смену 1 или 2.'
  }
  return null
}

/** Выбрана не та ротационная смена, что указана в календаре. */
export function brigadeShiftMismatchHint(
  dateKey: string,
  selectedShiftId: string,
  calendarMap: CalendarBrigadeMap,
  shiftsById: Map<string, Pick<ShiftRecord, 'name'>>,
): string | null {
  const brigadeId = resolveWorkingBrigade(dateKey, calendarMap)
  if (!brigadeId || !selectedShiftId || brigadeId === selectedShiftId) return null
  const calShift = shiftsById.get(brigadeId)
  const selShift = shiftsById.get(selectedShiftId)
  if (!calShift || !selShift) return null
  if (!isRotationShift(calShift) || !isRotationShift(selShift)) return null
  return `По календарю работает ${calShift.name}, а в табеле выбрана ${selShift.name}. Переключите смену — иначе состав будет пуст.`
}

export function brigadeLabel(
  dateKey: string,
  calendarMap: CalendarBrigadeMap,
  shiftsById: Map<string, Pick<ShiftRecord, 'name'>>,
): string {
  if (!resolvePlantIsWorking(dateKey, calendarMap)) return 'простой'
  const brigadeId = resolveWorkingBrigade(dateKey, calendarMap)
  if (!brigadeId) return '* бригада не задана'
  return shiftsById.get(brigadeId)?.name ?? 'бригада'
}

/**
 * Рабочий ли день для сотрудника с учётом бригады в календаре.
 * @param timesheetShiftByWorker — смена из записи табеля (замена на другую бригаду).
 */
export function isBrigadeWorkDay(
  dateKey: string,
  worker: Pick<ProductionWorkerRecord, 'id' | 'defaultShift' | 'position'>,
  calendarMap: CalendarBrigadeMap,
  shiftsById: Map<string, Pick<ShiftRecord, 'name'>>,
  timesheetShiftByWorker?: Map<string, string>,
): boolean {
  if (!resolvePlantIsWorking(dateKey, calendarMap)) return false

  const shiftId = worker.defaultShift || ''
  const shift = shiftId ? shiftsById.get(shiftId) : undefined

  if (shift && isShift5_2(shift)) {
    return isWeekdayMonFri(parseDateKey(dateKey))
  }
  if (isPositionShift5_2(worker.position || '')) {
    return isWeekdayMonFri(parseDateKey(dateKey))
  }

  const workingBrigade = resolveWorkingBrigade(dateKey, calendarMap)
  if (!workingBrigade) return false

  if (shift && isRotationShift(shift)) {
    if (shiftId === workingBrigade) return true
    const entryShift = timesheetShiftByWorker?.get(worker.id ?? '')?.trim()
    return entryShift === workingBrigade
  }

  if (shiftId && shiftId === workingBrigade) return true

  const entryShift = timesheetShiftByWorker?.get(worker.id ?? '')?.trim()
  if (entryShift === workingBrigade) return true

  return false
}
